import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, isWithinInterval } from "date-fns";
import {
  CalendarIcon,
  GiftIcon,
  DocumentDownloadIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/outline";

const MaturityReport = () => {
  const navigate = useNavigate();
  const [maturities, setMaturities] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: new Date(),
    to: addDays(new Date(), 30),
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMaturities();
  }, [dateRange]);

  const fetchMaturities = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData = [
        {
          _id: "1",
          planNumber: "JP2024001",
          customerName: "John Doe",
          phoneNumber: "9876543210",
          maturityDate: addDays(new Date(), 5),
          maturityValue: 125000,
          walletBalance: { valueInr: 120000, grams: 0 },
          status: "ACTIVE",
          daysToMaturity: 5,
        },
        {
          _id: "2",
          planNumber: "JP2024002",
          customerName: "Jane Smith",
          phoneNumber: "9876543211",
          maturityDate: addDays(new Date(), 15),
          maturityValue: 250000,
          walletBalance: { valueInr: 0, grams: 45 },
          status: "ACTIVE",
          daysToMaturity: 15,
        },
      ];
      setMaturities(mockData);
    } catch (error) {
      console.error("Failed to fetch maturities:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Maturity Report</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Upcoming plan maturities from {format(dateRange.from, "dd MMM")}{" "}
              to {format(dateRange.to, "dd MMM yyyy")}
            </p>
          </div>
          <button
            onClick={() => navigate("/jewellery/reports")}
            className="text-neutral-dark hover:text-text"
          >
            ← Back to Reports
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-5 w-5 text-neutral-dark" />
          <input
            type="date"
            value={format(dateRange.from, "yyyy-MM-dd")}
            onChange={(e) =>
              setDateRange({ ...dateRange, from: new Date(e.target.value) })
            }
            className="px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
          />
          <span className="text-neutral-dark">to</span>
          <input
            type="date"
            value={format(dateRange.to, "yyyy-MM-dd")}
            onChange={(e) =>
              setDateRange({ ...dateRange, to: new Date(e.target.value) })
            }
            className="px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
          />
          <button
            onClick={fetchMaturities}
            className="px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
          >
            Update
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-primary-light rounded-lg p-3 mr-4">
              <CalendarIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Total Maturing</p>
              <p className="text-2xl font-bold text-text">
                {maturities.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-success-light rounded-lg p-3 mr-4">
              <CurrencyRupeeIcon className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Total Value</p>
              <p className="text-2xl font-bold text-text">
                {formatAmount(
                  maturities.reduce((sum, m) => sum + m.maturityValue, 0)
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-warning-light rounded-lg p-3 mr-4">
              <GiftIcon className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">This Week</p>
              <p className="text-2xl font-bold text-text">
                {maturities.filter((m) => m.daysToMaturity <= 7).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Maturities List */}
      <div className="bg-background shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-neutral-light">
          <thead className="bg-neutral-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Plan Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Maturity Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Wallet Balance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Maturity Value
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-neutral-light">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : maturities.length > 0 ? (
              maturities.map((maturity) => (
                <tr key={maturity._id} className="hover:bg-neutral-light">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-text">
                      {maturity.planNumber}
                    </p>
                    <p className="text-xs text-neutral-dark">
                      {maturity.status}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-text">{maturity.customerName}</p>
                    <p className="text-xs text-neutral-dark">
                      {maturity.phoneNumber}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-text">
                      {format(new Date(maturity.maturityDate), "dd MMM yyyy")}
                    </p>
                    <p className="text-xs text-primary">
                      {maturity.daysToMaturity} days
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-text">
                      {maturity.walletBalance.grams > 0
                        ? `${maturity.walletBalance.grams} gms`
                        : formatAmount(maturity.walletBalance.valueInr)}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-bold text-success">
                      {formatAmount(maturity.maturityValue)}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() =>
                        navigate(`/jewellery/plans/${maturity._id}`)
                      }
                      className="text-primary hover:text-primary-dark"
                    >
                      View Plan
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="px-6 py-4 text-center text-neutral-dark"
                >
                  No maturities in selected date range
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MaturityReport;
